import { useEffect, useState } from "react";

import { ApiError, api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { useLang } from "../lib/i18n";
import { authCopy } from "../lib/i18n/auth";
import { ScreenShell } from "./screen-shell";
import { IconCheck } from "./ui/icon";
import { Eyebrow, H1, PrimaryButton } from "./ui/primitives";
import { SytoLogo } from "./ui/syto-logo";

// Verify-email confirmation screen. Visual parity with B_VerifyEmail in
// design_handoff_sytofood/screens-b2.jsx (~line 313-358): logo, big
// tangerine check tile, centered editorial heading, tangerine CTA.
// Three runtime states share the layout: verifying (skeleton tile),
// ok (filled check + auto-advance), error (muted tile + retry CTA).

type Props = {
  code: string;
  onDone: () => void;
};

type Status = "verifying" | "ok" | "error";

export function VerifyEmailScreen({ code, onDone }: Props) {
  const { refresh } = useAuth();
  const { lang } = useLang();
  const t = authCopy[lang];
  const [status, setStatus] = useState<Status>("verifying");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .verifyEmail(code)
      .then(async () => {
        if (cancelled) return;
        await refresh();
        setStatus("ok");
        setTimeout(() => {
          if (!cancelled) onDone();
        }, 1500);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setStatus("error");
        if (err instanceof ApiError) {
          setMessage(err.message);
        } else if (err instanceof Error) {
          setMessage(err.message);
        } else {
          setMessage(t.veCodeInvalidFallback);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code, refresh, onDone, t.veCodeInvalidFallback]);

  const isOk = status === "ok";
  const isError = status === "error";

  return (
    <ScreenShell hideHeader>
      <div style={{ marginBottom: 20 }}>
        <SytoLogo size={22} />
      </div>

      <div style={{ marginTop: 48, textAlign: "center" }}>
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: 24,
            background: isError ? "var(--bad-bg)" : "var(--accent)",
            color: isError ? "var(--bad-fg)" : "var(--accent-ink)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: isError ? "none" : "0 12px 32px var(--accent-glow)",
            opacity: status === "verifying" ? 0.35 : 1,
            transition: "opacity 200ms cubic-bezier(0.2, 0.7, 0.3, 1)",
          }}
        >
          {isError ? (
            <svg
              width="44"
              height="44"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          ) : (
            <IconCheck size={44} />
          )}
        </div>
      </div>

      <div style={{ marginTop: 28, textAlign: "center" }}>
        <Eyebrow>
          {status === "verifying"
            ? t.veEyebrowVerifying
            : isOk
              ? t.veEyebrowOk
              : t.veEyebrowError}
        </Eyebrow>
        <H1 accent="." className="mt-3">
          {status === "verifying"
            ? t.veHeadingVerifying
            : isOk
              ? t.veHeadingOk
              : t.veHeadingError}
        </H1>
        <p
          style={{
            marginTop: 14,
            fontSize: 14,
            color: "var(--ink-2)",
            lineHeight: 1.5,
            maxWidth: 320,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {status === "verifying"
            ? t.veBodyVerifying
            : isOk
              ? t.veBodyOk
              : (message ?? t.veBodyErrorFallback)}
        </p>
      </div>

      {isError ? (
        <PrimaryButton onClick={onDone} className="mt-8">
          {t.veRetryButton}
        </PrimaryButton>
      ) : null}
    </ScreenShell>
  );
}
